import { ApiError, getAccessToken } from './client'
import { formatUnitLabel } from '../utils/formatUnitLabel'

export type OrderBlankLineRequest = {
  catalogItemId: number
  quantity: number
  unit?: string
  unitPrice?: number
  note?: string
}

export const parseCartUnitPrice = (price: string | null | undefined): number | undefined => {
  if (price == null || price.trim() === '' || price.trim() === '—') {
    return undefined
  }
  const normalized = Number(price.trim().replace(',', '.'))
  return Number.isFinite(normalized) ? normalized : undefined
}

export type OrderBlankRequest = {
  organizationId: number
  privateSupplierId: number
  deliveryAddress?: string
  desiredDeliveryDate?: string
  desiredDeliveryTimeUntil?: string
  comment?: string
  lines: OrderBlankLineRequest[]
}

const orderBlankBody = (request: OrderBlankRequest) => ({
  organization_id: request.organizationId,
  private_supplier_id: request.privateSupplierId,
  delivery_address: request.deliveryAddress ?? null,
  desired_delivery_date: request.desiredDeliveryDate ?? null,
  desired_delivery_time_until: request.desiredDeliveryTimeUntil ?? null,
  comment: request.comment ?? null,
  lines: request.lines.map((line) => ({
    catalog_item_id: line.catalogItemId,
    quantity: line.quantity,
    unit: formatUnitLabel(line.unit),
    ...(line.unitPrice != null ? { unit_price: line.unitPrice } : {}),
    ...(line.note?.trim() ? { note: line.note.trim() } : {}),
  })),
})

const apiBase = import.meta.env.VITE_API_BASE_URL ?? 'https://api.provisium.ru/api/v1'

export const downloadOrderBlankPdf = async (request: OrderBlankRequest): Promise<Blob> => {
  const token = getAccessToken()
  const response = await fetch(`${apiBase}/buyer/cart/order-blank?format=pdf`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(orderBlankBody(request)),
  })
  if (!response.ok) {
    const text = await response.text().catch(() => '')
    throw new ApiError(response.status, text || response.statusText)
  }
  return response.blob()
}

/**
 * Бланк таблицей (XLSX).
 *
 * Третий формат рядом с PDF и текстом: таблицу поставщик открывает и работает с ней —
 * правит количества, считает, переносит к себе. Из PDF для этого приходится перенабирать.
 */
export const downloadOrderBlankSpreadsheet = async (request: OrderBlankRequest): Promise<Blob> => {
  const token = getAccessToken()
  const response = await fetch(`${apiBase}/buyer/cart/order-blank?format=xlsx`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(orderBlankBody(request)),
  })
  if (!response.ok) {
    const text = await response.text().catch(() => '')
    throw new ApiError(response.status, text || response.statusText)
  }
  return response.blob()
}

export const fetchOrderBlankText = async (request: OrderBlankRequest): Promise<string> => {
  const token = getAccessToken()
  const response = await fetch(`${apiBase}/buyer/cart/order-blank?format=text`, {
    method: 'POST',
    headers: {
      Accept: 'text/plain;charset=UTF-8',
      'Content-Type': 'application/json;charset=UTF-8',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(orderBlankBody(request)),
  })
  if (!response.ok) {
    const text = await response.text().catch(() => '')
    throw new ApiError(response.status, text || response.statusText)
  }
  // Decode explicitly as UTF-8 — do not rely on missing charset in Content-Type.
  const bytes = await response.arrayBuffer()
  return new TextDecoder('utf-8').decode(bytes)
}

export const copyOrderBlankText = async (request: OrderBlankRequest): Promise<void> => {
  const text = await fetchOrderBlankText(request)
  await navigator.clipboard.writeText(text)
}
